import { useState } from "react";
import { Plus, X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImageFile } from "./adminApi";

export function Label({ children }) {
  return <span className="block text-[10px] tracking-[0.18em] uppercase text-[#5a4b4b] mb-1.5">{children}</span>;
}

export function Text({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="inp" />
    </label>
  );
}

export function Area({ label, value, onChange, rows = 4, placeholder }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="inp resize-y" />
    </label>
  );
}

export function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="inp">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

export function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-2">
      <button type="button" data-testid={testId} data-state={value ? "on" : "off"} onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-[#14523d]" : "bg-[#d4af37]/40"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
      <span className="text-sm text-[#3b1a1a]">{label}</span>
    </label>
  );
}

export function ImageField({ label, value, onChange, folder = "General" }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImageFile(file, folder);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-3 items-start">
        <div className="w-24 h-20 shrink-0 border border-[#d4af37]/40 bg-[#faf8f5] overflow-hidden grid place-items-center">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-[#5a4b4b]">No image</span>}
        </div>
        <div className="flex-1 space-y-2">
          <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or upload" className="inp" />
          <label className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#7c1d1d] cursor-pointer border border-[#d4af37]/40 px-3 py-2 hover:bg-[#faf8f5]">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

// Editable list of strings
export function ListEditor({ label, items = [], onChange, placeholder = "Add item" }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mb-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="inp" />
        <button type="button" onClick={add} className="shrink-0 px-3 bg-[#7c1d1d] text-white"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 bg-[#faf8f5] border border-[#d4af37]/25 px-3 py-2">
            <span className="flex-1 text-sm text-[#3b1a1a]">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}><X className="w-4 h-4 text-[#7c1d1d]" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editable itinerary (day/title/description)
export function ItineraryEditor({ items = [], onChange }) {
  const add = () => onChange([...items, { day: items.length + 1, title: "", description: "" }]);
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const del = (i) => onChange(items.filter((_, j) => j !== i).map((it, idx) => ({ ...it, day: idx + 1 })));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><Label>Day-wise Itinerary</Label><button type="button" onClick={add} className="text-xs tracking-[0.15em] uppercase text-[#7c1d1d] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add Day</button></div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="border border-[#d4af37]/25 p-3 bg-[#faf8f5]">
            <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-[#7c1d1d]">Day {it.day}</span><button type="button" onClick={() => del(i)}><X className="w-4 h-4 text-[#7c1d1d]" /></button></div>
            <input value={it.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Day title" className="inp mb-2" />
            <textarea rows={2} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" className="inp resize-y" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Editable FAQ (question/answer)
export function FaqEditor({ items = [], onChange }) {
  const add = () => onChange([...items, { question: "", answer: "" }]);
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><Label>FAQs</Label><button type="button" onClick={add} className="text-xs tracking-[0.15em] uppercase text-[#7c1d1d] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add FAQ</button></div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="border border-[#d4af37]/25 p-3 bg-[#faf8f5]">
            <div className="flex justify-end mb-1"><button type="button" onClick={() => del(i)}><X className="w-4 h-4 text-[#7c1d1d]" /></button></div>
            <input value={it.question} onChange={(e) => upd(i, "question", e.target.value)} placeholder="Question" className="inp mb-2" />
            <textarea rows={2} value={it.answer} onChange={(e) => upd(i, "answer", e.target.value)} placeholder="Answer" className="inp resize-y" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Gallery editor (list of image URLs w/ upload)
export function GalleryEditor({ items = [], onChange, folder = "gallery" }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImageFile(f, folder));
      onChange([...items, ...urls]);
      toast.success(`${urls.length} image(s) added`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Gallery</Label>
        <label className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#7c1d1d] cursor-pointer">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
          <input type="file" accept="image/*" multiple onChange={onFile} className="hidden" />
        </label>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {items.map((url, i) => (
          <div key={i} className="relative group aspect-square border border-[#d4af37]/25 overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 bg-[#7c1d1d] text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
      <div className="absolute inset-0 bg-[#2d1616]/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-[#fdfbf7] border border-[#d4af37]/40 shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} my-4`}>
        <div className="sticky top-0 bg-[#fdfbf7] flex items-center justify-between px-6 py-4 border-b border-[#d4af37]/25 z-10">
          <h3 className="font-serif text-2xl text-[#3b1a1a]">{title}</h3>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center hover:bg-[#7c1d1d] hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
